// src/component/Signin/index.jsx

import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  IconButton,
  OutlinedInput,
  InputAdornment,
  FormControl,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Image from "../../asserts/signup.png";
import { NavLink, useNavigate } from "react-router-dom";
import { axiosInstance } from "../Api/instance";
import Image1 from "./google-icon.svg";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "../../FirebaseConfig";
import { PrimaryButton } from "../ui/Buttons";
import { styled } from "@mui/material/styles";

const PageContainer = styled(Grid)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: theme.palette.background.default,
  overflow: "hidden",
}));

const FormContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "48px 40px",
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    padding: "32px 20px",
  },
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  marginBottom: "32px",
  textAlign: "left",
}));

const ImageContainer = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1F2937' : '#F8FAFC',
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const SigninPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const Navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Custom toast state to replace browser alert()
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleChange = (event) => {
    setPassword(event.target.value);
  };

  useEffect(() => {
    localStorage.clear();
  }, []);

  const provider = new GoogleAuthProvider();

  const signInWithGoogle = async () => {
    localStorage.clear();
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const signedInUser = result.user;

      localStorage.setItem("username", signedInUser.displayName);
      localStorage.setItem("userMail", signedInUser.email);
      localStorage.setItem("profile", signedInUser.photoURL);

      console.log("Google Sign-In Successful", signedInUser.email);

      const payload = { email: signedInUser.email };
      const response = await axiosInstance.put(
        `put/U_googlelogin.php`,
        payload
      );

      if (response.data.message === "success") {
        if (response.data.message === "timeout") {
          Navigate("/signin");
          return;
        }
        const token = response.data.token;
        localStorage.setItem("token", token);
        localStorage.setItem("email", response.data.email);
        const plan = response.data.plan;
        localStorage.setItem("plan", plan);
        const category = response.data.plan_category;
        localStorage.setItem("category", category);
        const expiryDate = new Date(response.data.plan_expiry_date);
        const currentDate = new Date();

        if (currentDate > expiryDate) {
          showToast("Your premium plan has expired. Please purchase a new plan.", "warning");
          setTimeout(() => Navigate("/home"), 2000);
        } else {
          Navigate("/home");
        }
      } else if (
        response.data.error === "you are blocked..so contact your admin"
      ) {
        showToast("You are blocked. Please contact your admin.", "error");
      }
    } catch (error) {
      console.error("Google Sign-In Error", error);
      showToast("Google Sign-In failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      showToast("Please fill in all fields", "warning");
      return;
    }
    localStorage.clear();
    setLoading(true);
    try {
      if (password.length >= 6 || password.length === 0) {
        const response = await axiosInstance.post(`put/U_login.php`, {
          email: email,
          password: password,
        });

        if (response.data.message === "success") {
          if (response.data.message === "timeout") {
            Navigate("/signin");
            return;
          }
          const category = response.data.plan_category;
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("email", response.data.email);
          localStorage.setItem("category", category);
          const plan = response.data.plan;
          localStorage.setItem("plan", plan);

          const courseplan = response.data.course_plan_category;
          localStorage.setItem("course_category", courseplan);
          localStorage.setItem("userMail", email);
          
          const expiryDate = new Date(response.data.plan_expiry_date);
          const currentDate = new Date();

          if (currentDate > expiryDate) {
            showToast("Your premium plan has expired. Please purchase a new plan.", "warning");
            setTimeout(() => Navigate("/home"), 2000);
          } else {
            Navigate("/home");
          }
        } else if (
          response.data.error === "you are blocked..so contact your admin"
        ) {
          showToast("You are blocked. Please contact your admin.", "error");
        } else {
          showToast("Username or Password is Invalid", "error");
        }
      } else {
        showToast("Password must be at least 6 characters long", "warning");
      }
    } catch (error) {
      console.error("Login Error", error);
      showToast("Login failed. Please check your credentials and try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer container>
      {/* Toast Notification */}
      <Snackbar 
        open={toast.open} 
        autoHideDuration={4000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setToast(prev => ({ ...prev, open: false }))} 
          severity={toast.severity} 
          sx={{ width: "100%", borderRadius: "12px", boxShadow: 3 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Form Left Panel */}
      <FormContainer item xs={12} sm={6}>
        <Box style={{ maxWidth: "420px", width: "100%", margin: "0 auto" }}>
          <BrandHeader>
            <Typography variant="h2" style={{ fontWeight: 800, color: "var(--color-primary)", marginBottom: "8px" }}>
              NURSING UPDATE
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Welcome back! Please enter your credentials to login.
            </Typography>
          </BrandHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            <Box style={{ marginBottom: "20px" }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                Email
                <EmailIcon fontSize="inherit" color="action" />
              </Typography>
              <TextField
                fullWidth
                id="email"
                type="email"
                placeholder="nursing@example.com"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                required
              />
            </Box>

            <Box style={{ marginBottom: "24px" }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                Password
                <LockIcon fontSize="inherit" color="action" />
              </Typography>
              <FormControl fullWidth variant="outlined">
                <OutlinedInput
                  id="outlined-adornment-password"
                  type={showPassword ? "text" : "password"}
                  onChange={handleChange}
                  value={password}
                  placeholder="••••••••"
                  required
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end" aria-label="Toggle password visibility">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>

            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
              <NavLink to="/otp" style={{ textDecoration: "none", color: "var(--color-secondary)", fontWeight: 600, fontSize: "0.875rem" }}>
                Forgot Password?
              </NavLink>
            </Box>

            <PrimaryButton
              type="submit"
              loading={loading}
              style={{ width: "100%", height: "46px", marginBottom: "16px" }}
            >
              Sign In
            </PrimaryButton>
          </form>

          <Box style={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
            <Box style={{ flex: 1, height: "1px", backgroundColor: "var(--color-border)" }} />
            <Typography variant="caption" color="textSecondary" style={{ padding: "0 16px", fontWeight: 500 }}>
              OR
            </Typography>
            <Box style={{ flex: 1, height: "1px", backgroundColor: "var(--color-border)" }} />
          </Box>

          <PrimaryButton
            variant="outlined"
            onClick={signInWithGoogle}
            disabled={loading}
            style={{ 
              width: "100%", 
              height: "46px", 
              backgroundColor: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <img
              src={Image1}
              alt=""
              style={{ width: "20px", height: "20px" }}
            />
            Continue with Google
          </PrimaryButton>

          <Typography variant="body1" style={{ textAlign: "center", marginTop: "32px", fontSize: "0.875rem" }}>
            Don't have an account?{" "}
            <NavLink to="/signup" style={{ textDecoration: "none", color: "var(--color-primary)", fontWeight: 700 }}>
              Sign Up
            </NavLink>
          </Typography>
        </Box>
      </FormContainer>

      {/* Decorative Right Panel */}
      <ImageContainer item xs={12} sm={6}>
        <img
          src={Image}
          alt="Nursing Update Mock Examination"
          style={{
            width: "90%",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: "20px",
          }}
        />
      </ImageContainer>
    </PageContainer>
  );
};

export default SigninPage;
